import type { PresetFormValues } from '../../../lib/types';
import { ensureDefaultContactFormFields } from '../../../lib/contact-form-fields';
import { WIDGET_TRANSLATIONS, type WidgetLang } from '../../../lib/widget-translations';
import type { PreviewMessage, WidgetPreviewConfig } from '../types';

interface PreviewDemoCatalog {
  interestedUserMessage: string;
  contactFormAnswer: string;
  botResponses: string[];
  messageInputAriaLabel: string;
  assistantTypingAriaLabel: string;
  keywordRules: WidgetPreviewConfig['demo']['keywordRules'];
}

const PREVIEW_DEMO_CATALOG: Record<WidgetLang, PreviewDemoCatalog> = {
  en: {
    interestedUserMessage: 'This sounds interesting. Could you tell me more?',
    contactFormAnswer:
      'Of course! The widget helps answer customers, collect inquiries, and pass them to your team.',
    botResponses: [
      'Thanks for your message! Let me tell you more.',
      "I understand your question. Let's figure it out together.",
      'That is a good question. What matters most to you?',
      'I can help with that. Choose an option below.',
      'Thank you! I have passed the information to a specialist.',
    ],
    messageInputAriaLabel: 'Message',
    assistantTypingAriaLabel: 'Assistant is typing',
    keywordRules: [
      {
        keywords: ['price', 'cost'],
        response: 'The price depends on the selected program. Which format interests you?',
      },
      {
        keywords: ['course', 'training'],
        response: 'I can tell you about the training. We offer several programs and formats.',
      },
      {
        keywords: ['help'],
        response: 'Of course! Please describe your task in a little more detail.',
      },
    ],
  },
  ru: {
    interestedUserMessage: 'Звучит интересно. Расскажите, пожалуйста, подробнее.',
    contactFormAnswer:
      'Конечно! Виджет помогает отвечать клиентам, собирать обращения и передавать их вашей команде.',
    botResponses: [
      'Спасибо за сообщение! Сейчас расскажу подробнее.',
      'Понял ваш вопрос. Давайте разберемся вместе.',
      'Это хороший вопрос. Уточните, пожалуйста, что для вас важнее всего?',
      'Я могу помочь с этим. Выберите подходящий вариант ниже.',
      'Спасибо! Передал информацию специалисту.',
    ],
    messageInputAriaLabel: 'Сообщение',
    assistantTypingAriaLabel: 'Ассистент печатает',
    keywordRules: [
      {
        keywords: ['цена', 'стоимость'],
        response:
          'Стоимость зависит от выбранной программы. Подскажите, какой формат вам интересен?',
      },
      {
        keywords: ['курс', 'обучение'],
        response: 'Расскажу об обучении. Есть несколько программ и удобные форматы занятий.',
      },
      {
        keywords: ['помощь', 'помоги'],
        response: 'Конечно, помогу! Опишите задачу чуть подробнее.',
      },
    ],
  },
};

const getPreviewMessages = (
  firstMessage: string,
  interestedUserMessage: string,
  contactFormAnswer?: string,
): PreviewMessage[] => [
  { id: 'demo-assistant-1', sender: 'assistant', text: firstMessage },
  { id: 'demo-user-1', sender: 'user', text: interestedUserMessage },
  ...(contactFormAnswer
    ? [{ id: 'demo-assistant-form-answer', sender: 'assistant' as const, text: contactFormAnswer }]
    : []),
];

export const createWidgetPreviewConfig = (
  settings: PresetFormValues,
  lang: WidgetLang = 'ru',
): WidgetPreviewConfig => {
  const translations = WIDGET_TRANSLATIONS[lang];
  const demoCatalog = PREVIEW_DEMO_CATALOG[lang];

  return {
    lang,
    translations,
    policyUrl: settings.personal_data_policy_url,
    appearance: {
      brandColor: settings.appearance.brand_color || '#234eee',
      buttonTheme: settings.appearance.button_theme,
      buttonBackgroundColor: settings.appearance.button_background_color || '#ffffff',
      buttonTextColor: settings.appearance.button_text_color || '#02071d',
      widgetIcon: {
        type: settings.appearance.widget_icon.type,
        customUrl: settings.appearance.widget_icon.custom_url || null,
      },
      fontFamily: settings.appearance.font_family || 'Inter Tight',
    },
    content: {
      buttonText: settings.content.button_text || translations.triggerLabel,
      assistantName: settings.content.assistant_name || translations.welcomeAssistantName,
      welcomeMessage: settings.content.welcome_message || translations.defaultFirstMessage,
      quickQuestions: [...settings.content.quick_questions],
    },
    preview: {
      mode: settings.preview.mode,
      showDelaySeconds: settings.preview.show_delay_seconds,
      staticQuestion: settings.content.welcome_message || translations.defaultFirstMessage,
      staticAnswers: [
        ...(settings.preview.static_answers.some((answer) => answer.trim())
          ? settings.preview.static_answers
          : settings.content.quick_questions),
      ],
    },
    contactForm: {
      mode: settings.advanced.contact_form.mode,
      messageBeforeForm: settings.advanced.contact_form.show_close_button
        ? demoCatalog.contactFormAnswer
        : settings.advanced.contact_form.message_before_form,
      showAfterMessages: settings.advanced.contact_form.show_after_messages,
      showCloseButton: settings.advanced.contact_form.show_close_button ?? false,
      showAfterAnswer: settings.advanced.contact_form.show_form_after_answer ?? false,
      triggerEvents: [...settings.advanced.contact_form.trigger_events],
      fields: ensureDefaultContactFormFields(
        settings.advanced.contact_form.fields,
        translations,
      ).map((field) => ({
        ...field,
        options: [...field.options],
      })),
    },
    messages: {
      borderRadius: settings.advanced.messages.border_radius,
      tailEnabled: settings.advanced.messages.tail_enabled,
      showAgentAnswerHints: settings.advanced.messages.show_agent_answer_hints,
    },
    frame: {
      widthPx: settings.advanced.widget.width_px,
      heightPx: settings.advanced.widget.height_px,
      borderWidthPx: settings.advanced.widget.border_width_px,
      borderColor: settings.advanced.widget.border_color,
    },
    trigger: {
      borderRadius: settings.advanced.button.border_radius,
      widthPx: settings.advanced.button.width_px,
      heightPx: settings.advanced.button.height_px,
      borderWidthPx: settings.advanced.button.border_width_px,
      borderColor: settings.advanced.button.border_color,
    },
    welcome: {
      messageBorderRadius: settings.advanced.preview.message_border_radius,
      hintBorderRadius: settings.advanced.preview.hint_border_radius,
      showAgentAnswerHints: settings.advanced.preview.show_agent_answer_hints,
    },
    position: {
      desktop: {
        placement: settings.advanced.position.desktop.placement,
        sideOffset: settings.advanced.position.desktop.side_offset,
        bottomOffset: settings.advanced.position.desktop.bottom_offset,
        unit: settings.advanced.position.desktop.unit || 'px',
      },
      mobile: {
        placement: settings.advanced.position.mobile.placement,
        sideOffset: settings.advanced.position.mobile.side_offset,
        bottomOffset: settings.advanced.position.mobile.bottom_offset,
        unit: settings.advanced.position.mobile.unit || 'px',
      },
    },
    demo: {
      initialMessages: getPreviewMessages(
        settings.content.welcome_message || translations.defaultFirstMessage,
        demoCatalog.interestedUserMessage,
        settings.advanced.contact_form.show_form_after_answer
          ? demoCatalog.contactFormAnswer
          : undefined,
      ),
      botResponses: [...demoCatalog.botResponses],
      messageInputAriaLabel: demoCatalog.messageInputAriaLabel,
      assistantTypingAriaLabel: demoCatalog.assistantTypingAriaLabel,
      responseStrategy: 'sequential',
      responseDelayMs: 900,
      keywordRules: demoCatalog.keywordRules.map((rule) => ({
        ...rule,
        keywords: [...rule.keywords],
      })),
      fixedResponseIndex: 0,
      showTyping: true,
      showContactFormInitially: true,
    },
  };
};
